/**
 * Text generator — extruded 3D text driven by user-typed copy.
 *
 * Two fonts ship with the build, exposed as a sans-serif / serif segmented
 * control. Glyph outlines are sampled from opentype.js, partitioned into
 * outer outlines and holes by even-odd containment depth, and extruded
 * via `buildExtrudedPolygon` so the produced topology is identical in
 * shape to every other extruded primitive in the codebase (clean cap
 * triangulation, quad side walls, recomputed loop normals).
 *
 * Origin is the baseline-left of the first character, matching Blender's
 * convention. Output lives in the XY plane with extrusion along +Z.
 */

import { type FontFamily, getFont } from "./text/fonts.ts";
import { buildTextGeometry } from "./text/textGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  defineGenerator,
  numParam,
  paramsFromSpecs,
  stringParam,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface TextParams extends GeneratorParams {
  text: string;
  font: FontFamily;
  size: number;
  extrusion: number;
  characterSpacing: number;
  lineHeight: number;
}

const TEXT_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "text",
    label: "Text",
    type: "string",
    display: "multiline",
    default: "Text",
    placeholder: "Enter text…",
    // Sanity ceiling on character count. Well above any reasonable
    // signage / titling use; pathological inputs (pasted novels) get
    // truncated at the input layer rather than locking the worker on a
    // multi-second build.
    maxLength: 5000,
  },
  {
    id: "font",
    label: "Font",
    type: "enum",
    display: "segmented",
    default: "sans-serif",
    options: [
      { value: "sans-serif", label: "Sans" },
      { value: "serif", label: "Serif" },
    ],
  },
  {
    id: "size",
    label: "Size",
    type: "number",
    unit: "meters",
    default: 0.2,
    min: 0.01,
    max: 5,
    step: 0.01,
    description: "Em size — one em maps to this many metres of XY extent.",
  },
  {
    id: "extrusion",
    label: "Extrusion",
    type: "number",
    unit: "meters",
    default: 0.05,
    min: 0.001,
    max: 1,
    step: 0.005,
    description:
      "Depth along +Z. Set low for inscribed text, high for raised signage.",
  },
  {
    id: "characterSpacing",
    label: "Spacing",
    type: "number",
    default: 0,
    min: -0.2,
    max: 0.5,
    step: 0.01,
    description:
      "Extra advance per character, as a fraction of one em (typographic tracking).",
  },
  {
    id: "lineHeight",
    label: "Line Height",
    type: "number",
    default: 1.0,
    min: 0.5,
    max: 3,
    step: 0.05,
    description:
      "Multiplier of the font's intrinsic line height. 1.0 = single-spaced.",
  },
];

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function coerceFont(value: string): FontFamily {
  return value === "serif" ? "serif" : "sans-serif";
}

function buildText(
  p: TextParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const family = coerceFont(stringParam(p, "font", "sans-serif"));
  const font = getFont(family);
  const geometry = buildTextGeometry(
    {
      text: stringParam(p, "text", "Text"),
      size: numParam(p, "size", 0.2),
      extrusion: numParam(p, "extrusion", 0.05),
      characterSpacing: numParam(p, "characterSpacing", 0),
      lineHeight: numParam(p, "lineHeight", 1.0),
    },
    font,
  );
  return { kind: "mesh", geometry };
}

// ---------------------------------------------------------------------------
// Geometry signature
// ---------------------------------------------------------------------------

/**
 * Identity token for everything the build pulls into geometry: the text
 * itself, the font choice, and every numeric param. Numbers are
 * quantised to 1e-6 so sub-micrometre float drift between rebuilds
 * doesn't spuriously invalidate the cache. The unit-separator joiner
 * (\x1f) cannot occur inside any user-typed line, eliminating ambiguity
 * with the text param.
 */
function textGeometrySignature(p: TextParams): string {
  const q = (n: number) => Math.round(n * 1e6) / 1e6;
  return [
    stringParam(p, "text", "Text"),
    stringParam(p, "font", "sans-serif"),
    q(numParam(p, "size", 0.2)),
    q(numParam(p, "extrusion", 0.05)),
    q(numParam(p, "characterSpacing", 0)),
    q(numParam(p, "lineHeight", 1.0)),
  ].join("\x1f");
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const TEXT_GENERATOR: Generator = defineGenerator<TextParams>({
  id: "text",
  label: "Text",
  category: "prop",
  paramSpecs: TEXT_SPECS,
  defaultParams: paramsFromSpecs(TEXT_SPECS) as TextParams,
  build: buildText,
  geometrySignature: textGeometrySignature,
});
